import { useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FlashMessage from "../../components/FlashMessage";
import { LoginWrapper } from "../Wrapper";
import useStyles from "./styles";
import RegistrationIcon from "../../assets/images/logo.png";
import { Avatar } from "@mui/material";
import OtpInput from "react-otp-input";
import { forgotPassword, verifyOtp } from "../../apollo/server";

const FORGOT_PASSWORD = gql`
  ${forgotPassword}
`;
const VERIFY_OTP = gql`
  ${verifyOtp}
`;
function VerifyForgotOtp() {
  const theme = useTheme();
  const classes = useStyles();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(30);
  const [otpError, setOtpError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendOtp] = useMutation(FORGOT_PASSWORD, { onCompleted, onError });
  const [verifyOtpMutation] = useMutation(VERIFY_OTP);
  const { state } = useLocation();
  useEffect(() => {
    const myInterval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        clearInterval(myInterval);
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  });

  useEffect(() => {
    if (!state?.email) return;
    // If the previous screen already sent the OTP (success path), skip the
    // initial send here so we don't trigger a second code + overwrite the first.
    if (state?.skipInitialSend) {
      setSeconds(30);
      return;
    }
    sendOtp({ variables: { email: state.email, otp: "" } });
    setSeconds(30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.email]);

  function onCompleted() {
    try {
      setLoading(true);
    } catch (e) {
      setError("Something went wrong");
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  function onError(e) {
    setLoading(false);
    setError(e.message);
  }

  const onCodeFilled = async (code) => {
    setOtpError(false);
    setError("");
    try {
      await verifyOtpMutation({
        variables: { identifier: state?.email, channel: "EMAIL", purpose: "FORGOT_PASSWORD", code },
      });
      navigate("/new-password", {
        replace: true,
        state: { email: state?.email, code },
      });
    } catch (e) {
      setOtpError(true);
      setError(e.message || "Incorrect code");
    }
  };

  const resendOtp = () => {
    setError("");
    setOtpError(false);
    sendOtp({ variables: { email: state?.email, otp: "" } });
    setSeconds(30);
  };
  const handleCode = (val) => {
    const code = val;
    setOtp(val);
    if (code.length === 6) {
      onCodeFilled(code);
    }
  };

  return (
    <LoginWrapper>
      <FlashMessage
        open={Boolean(error)}
        severity={"error"}
        alertMessage={error}
      />
      <Box display="flex">
        <Box m="auto" sx={{ width: 90, height: 64, overflow: "hidden" }}>
          <img
            alt="RushMyFood"
            src={RegistrationIcon}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "top",
              transform: "scale(1.55)",
              transformOrigin: "top center",
            }}
          />
        </Box>
      </Box>
      <Typography variant="h5" className={classes.font700}>
        Forgot password ?
      </Typography>
      <Box mt={theme.spacing(1)} />
      <Typography
        variant="caption"
        className={`${classes.caption} ${classes.fontGrey}`}
      >
        Please enter the otp we sent to your <br />
        email
      </Typography>
      <Box display="flex">
        <Box m="auto">
          <OtpInput
            value={otp}
            onChange={handleCode}
            numInputs={6}
            containerStyle={{
              width: "100%",
              display: "flex",
              alignSelf: "center",
            }}
            inputStyle={{
              width: 45,
              height: 45,
              margin: 5,
              borderRadius: 5,
              fontSize: theme.typography.h2,
              border: "none",
              boxShadow: theme.shadows[3],
            }}
            focusStyle={{
              outlineColor: theme.palette.grey[900],
            }}
            editable
            renderInput={(props) => <input {...props} />}
          />
          <Box mt={2} />
          {otpError && (
            <Typography variant={"h6"} style={{ color: "red", fontSize: 14 }}>
              Invalid code, please check and enter again
            </Typography>
          )}
        </Box>
      </Box>
      <Box mt={theme.spacing(8)} />
      <Button
        variant="contained"
        fullWidth
        type="email"
        disableElevation
        disabled={seconds !== 0}
        className={classes.btnBase}
        onClick={(e) => {
          e.preventDefault();
          resendOtp();
        }}
      >
        {loading ? (
          <CircularProgress color="primary" />
        ) : (
          <Typography
            variant="caption"
            className={`${classes.caption} ${classes.font700}`}
          >
            Resend Code
          </Typography>
        )}
      </Button>
      <Box mt={theme.spacing(2)} />
      <Typography variant="caption" className={`${classes.caption}`}>
        {seconds !== 0 ? `Retry after ${seconds}s` : ""}
      </Typography>
    </LoginWrapper>
  );
}

export default VerifyForgotOtp;
